import React, { useEffect, useState } from 'react';
import { axios, useAuth } from '../../context/AuthContext';
import { useVeliOgrenci } from '../../context/VeliOgrenciContext';
import VeliOgrenciSecici from '../../components/parent/VeliOgrenciSecici';

const tarihFormatla = (tarih) => {
  if (!tarih) return '—';
  const parsed = new Date(tarih);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('tr-TR');
};

const sayiFormatla = (sayi) => {
  const parsed = Number(sayi);
  return Number.isFinite(parsed) && parsed > 0 ? new Intl.NumberFormat('tr-TR').format(parsed) : null;
};

const netFormatla = (sayi) => {
  const parsed = Number(sayi);
  return Number.isFinite(parsed) ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(parsed) : '—';
};

const SiraKarti = ({ baslik, sira, toplam, renk, ikon }) => {
  const siraMetni = sayiFormatla(sira);
  const toplamMetni = sayiFormatla(toplam);

  return (
    <div style={{
      minWidth: 0,
      padding: '18px 20px',
      borderRadius: 18,
      color: '#fff',
      background: renk,
      boxShadow: '0 10px 24px rgba(30, 41, 59, 0.12)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: '0.9rem', opacity: 0.95 }}>
        <span style={{ fontSize: '1.15rem' }}>{ikon}</span>{baslik}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 7, marginTop: 9 }}>
        <strong style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', lineHeight: 1.05 }}>
          {siraMetni ? `#${siraMetni}` : '—'}
        </strong>
        {toplamMetni && <span style={{ fontWeight: 700, opacity: 0.92 }}> / {toplamMetni} kişi</span>}
      </div>
      <div style={{ marginTop: 7, fontSize: '0.82rem', fontWeight: 600, opacity: 0.9 }}>
        {siraMetni && toplamMetni
          ? `${toplamMetni} katılımcı içinde ${siraMetni}. sırada`
          : siraMetni ? `${siraMetni}. sırada` : 'Sıra bilgisi bulunmuyor'}
      </div>
    </div>
  );
};

export default function TurkiyeGeneliSonuclarPage() {
  const { user } = useAuth();
  const { seciliOgrenci, birdenFazla } = useVeliOgrenci();
  const [ogrenciler, setOgrenciler] = useState([]);
  const [sonuclar, setSonuclar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let aktif = true;
    axios.get('/turkiye-geneli-deneme-sonuclari/benim')
      .then(({ data }) => {
        if (!aktif) return;
        setOgrenciler(data?.data?.ogrenciler || []);
        setSonuclar(data?.data?.sonuclar || []);
      })
      .catch((err) => {
        if (aktif) setError(err.response?.data?.message || 'Sonuçlar yüklenemedi.');
      })
      .finally(() => { if (aktif) setLoading(false); });
    return () => { aktif = false; };
  }, []);

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  const goruntulenecekSonuclar = (user?.rol === 'veli' && birdenFazla && seciliOgrenci?.id)
    ? sonuclar.filter((s) => Number(s.ogrenci_id) === Number(seciliOgrenci.id))
    : sonuclar;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Veli ve birden fazla öğrenci varsa kartlar şeklinde görünür */}
      {user?.rol === 'veli' && <VeliOgrenciSecici />}

      <header>
        <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.35rem' }}>🇹🇷 Türkiye Geneli Deneme Sonuçları</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 5 }}>
          {seciliOgrenci && user?.rol === 'veli' && birdenFazla
            ? `${seciliOgrenci.ad} ${seciliOgrenci.soyad} adlı öğrencinizin sınav sonuçları gösterilmektedir.`
            : 'Yalnızca hesabınıza bağlı öğrenci sonuçları gösterilir.'}
        </p>
      </header>

      {error && <div className="card" role="alert">{error}</div>}
      {!error && ogrenciler.length === 0 && (
        <div className="card"><div className="empty-state"><h3>Hesabınıza bağlı öğrenci bulunamadı</h3></div></div>
      )}
      {!error && ogrenciler.length > 0 && goruntulenecekSonuclar.length === 0 && (
        <div className="card"><div className="empty-state"><h3>{seciliOgrenci && birdenFazla ? `${seciliOgrenci.ad} için henüz Türkiye geneli deneme sonucu yok` : 'Henüz Türkiye geneli deneme sonucu yok'}</h3></div></div>
      )}

      {goruntulenecekSonuclar.map((sonuc) => {
        const ogrenci = ogrenciler.find((item) => Number(item.id) === Number(sonuc.ogrenci_id));
        const raporOzeti = sonuc.alt_dersler?.__rapor || {};
        const dersKayitlari = Object.entries(sonuc.dersler || {}).filter(([ders]) => ders !== '__rapor');
        const konuKayitlari = Object.entries(sonuc.konu_analizi || {}).filter(([, konular]) => Array.isArray(konular) && konular.length > 0);
        const resmiGenelKatilimci = raporOzeti.genel_katilimci_sayisi || (sonuc.tur === 'LGS' ? null : sonuc.toplam_ogrenci);
        const resmiKurumKatilimci = raporOzeti.kurum_katilimci_sayisi || (sonuc.tur === 'LGS' ? null : sonuc.kurum_ogrencisi_sayisi);
        const ortalamalar = dersKayitlari.reduce((result, [, info]) => ({
          sinif: result.sinif + (Number(info?.sinif_ortalamasi) || 0),
          kurum: result.kurum + (Number(info?.kurum_ortalamasi) || 0),
          genel: result.genel + (Number(info?.genel_ortalama) || 0),
        }), { sinif: 0, kurum: 0, genel: 0 });
        const genelNetFarki = Number(sonuc.toplam_net) - ortalamalar.genel;
        const yanlisKonular = konuKayitlari.flatMap(([ders, konular]) => konular
          .filter((konu) => Number(konu.yanlis) > 0)
          .map((konu) => ({ ders, ...konu })));
        const dogruYanlisVar = dersKayitlari.some(([, info]) => (
          info && typeof info === 'object' && (info.dogru !== undefined || info.d !== undefined || info.yanlis !== undefined || info.y !== undefined)
        ));
        const toplamDogru = dersKayitlari.reduce((toplam, [, info]) => toplam + Number(info?.dogru ?? info?.d ?? 0), 0);
        const toplamYanlis = dersKayitlari.reduce((toplam, [, info]) => toplam + Number(info?.yanlis ?? info?.y ?? 0), 0);
        return (
          <section
            className="card"
            key={`${sonuc.yukleme_id}-${sonuc.ogrenci_id}`}
            style={{ padding: 0, overflow: 'hidden', border: '1px solid #dbe5ff', boxShadow: '0 14px 36px rgba(38, 72, 140, 0.10)' }}
          >
            <div style={{ padding: '22px 24px', background: 'linear-gradient(115deg, #eef2ff 0%, #f5f3ff 55%, #fff7ed 100%)', borderBottom: '1px solid #e4e9f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <span style={{ display: 'inline-flex', padding: '5px 10px', borderRadius: 999, color: '#4338ca', background: '#e0e7ff', fontSize: '0.74rem', fontWeight: 900, letterSpacing: '0.04em' }}>
                    🇹🇷 TÜRKİYE GENELİ · {sonuc.tur || sonuc.yukleme_turu || 'DENEME'}
                  </span>
                  <h3 style={{ margin: '10px 0 0', fontSize: '1.25rem', color: '#172554' }}>{sonuc.baslik}</h3>
                  <div style={{ color: '#64748b', marginTop: 5, fontWeight: 600 }}>
                    {ogrenci ? `${ogrenci.ad} ${ogrenci.soyad}` : sonuc.ad_soyad}
                    {sonuc.sinif_adi ? ` · ${sonuc.sinif_adi}` : ''}
                  </div>
                </div>
                <span style={{ padding: '8px 12px', borderRadius: 12, color: '#475569', background: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>
                  📅 {tarihFormatla(sonuc.sinav_tarihi)}
                </span>
              </div>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
                {raporOzeti.sinif_sirasi != null && (
                  <SiraKarti
                    baslik="Sınıf sırası"
                    sira={raporOzeti.sinif_sirasi}
                    toplam={raporOzeti.sinif_katilimci_sayisi}
                    ikon="🏫"
                    renk="linear-gradient(135deg, #0891b2, #06b6d4)"
                  />
                )}
                <SiraKarti
                  baslik="Türkiye sırası"
                  sira={sonuc.genel_siralama}
                  toplam={resmiGenelKatilimci}
                  ikon="🏆"
                  renk="linear-gradient(135deg, #2563eb, #4f46e5)"
                />
                <SiraKarti
                  baslik="Kurum sırası"
                  sira={sonuc.okul_sirasi}
                  toplam={resmiKurumKatilimci}
                  ikon="🎖️"
                  renk="linear-gradient(135deg, #9333ea, #c026d3)"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginTop: 14 }}>
                <div style={{ padding: '14px 16px', borderRadius: 14, background: '#ecfdf5', border: '1px solid #bbf7d0' }}>
                  <div style={{ color: '#047857', fontSize: '0.8rem', fontWeight: 800 }}>📈 Toplam net</div>
                  <strong style={{ display: 'block', marginTop: 4, color: '#065f46', fontSize: '1.35rem' }}>{netFormatla(sonuc.toplam_net)}</strong>
                </div>
                <div style={{ padding: '14px 16px', borderRadius: 14, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <div style={{ color: '#c2410c', fontSize: '0.8rem', fontWeight: 800 }}>⭐ Puan</div>
                  <strong style={{ display: 'block', marginTop: 4, color: '#9a3412', fontSize: '1.35rem' }}>{sonuc.puan ?? '—'}</strong>
                </div>
                {ortalamalar.genel > 0 && (
                  <div style={{ padding: '14px 16px', borderRadius: 14, background: genelNetFarki >= 0 ? '#eff6ff' : '#fff1f2', border: `1px solid ${genelNetFarki >= 0 ? '#bfdbfe' : '#fecdd3'}` }}>
                    <div style={{ color: '#334155', fontSize: '0.8rem', fontWeight: 800 }}>📊 Türkiye genel net ortalaması</div>
                    <strong style={{ display: 'block', marginTop: 4, color: '#172554', fontSize: '1.25rem' }}>{netFormatla(ortalamalar.genel)}</strong>
                    <span style={{ display: 'block', marginTop: 4, color: genelNetFarki >= 0 ? '#047857' : '#b91c1c', fontSize: '0.78rem', fontWeight: 900 }}>
                      {genelNetFarki >= 0 ? 'Ortalamanın üstünde' : 'Ortalamanın altında'} · {netFormatla(Math.abs(genelNetFarki))} net
                    </span>
                  </div>
                )}
              </div>
            </div>

            {dersKayitlari.length > 0 && (
              <div style={{ margin: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {dogruYanlisVar && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
                    <span style={{ padding: '7px 11px', borderRadius: 10, color: '#047857', background: '#d1fae5', fontWeight: 900, fontSize: '0.82rem' }}>Toplam doğru: {toplamDogru}</span>
                    <span style={{ padding: '7px 11px', borderRadius: 10, color: '#b91c1c', background: '#fee2e2', fontWeight: 900, fontSize: '0.82rem' }}>Toplam yanlış: {toplamYanlis}</span>
                  </div>
                )}

                <section style={{ padding: 16, borderRadius: 14, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px', color: '#172554' }}>📚 Ders sonuçları ve ortalamalar</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse', fontSize: '0.78rem', textAlign: 'center' }}>
                      <thead><tr style={{ background: '#e8eefc', color: '#334155' }}>
                        {['Ders', 'Soru', 'Doğru', 'Yanlış', 'Boş', 'Net', 'Başarı', 'Sınıf ort.', 'Kurum ort.', 'Genel ort.'].map((label) => <th key={label} style={{ padding: '10px 8px', borderBottom: '1px solid #cbd5e1' }}>{label}</th>)}
                      </tr></thead>
                      <tbody>{dersKayitlari.map(([ders, sonucDers]) => {
                        const bilgi = sonucDers && typeof sonucDers === 'object' ? sonucDers : { net: sonucDers };
                        const dogru = Number(bilgi.dogru ?? bilgi.d ?? 0);
                        const yanlis = Number(bilgi.yanlis ?? bilgi.y ?? 0);
                        const soru = Number(bilgi.soru || 0);
                        const bos = Math.max(0, soru - dogru - yanlis);
                        return (
                          <tr key={ders}>
                            <th style={{ padding: '10px 8px', textAlign: 'left', color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>{ders}</th>
                            {[soru || '—', dogru, yanlis, bos, netFormatla(bilgi.net), bilgi.basari != null ? `%${netFormatla(bilgi.basari)}` : '—', netFormatla(bilgi.sinif_ortalamasi), netFormatla(bilgi.kurum_ortalamasi), netFormatla(bilgi.genel_ortalama)].map((value, index) => (
                              <td key={`${ders}-${index}`} style={{ padding: '10px 8px', borderBottom: '1px solid #e2e8f0', color: index === 2 && yanlis ? '#b91c1c' : '#334155', fontWeight: index === 2 && yanlis ? 900 : 700 }}>{value}</td>
                            ))}
                          </tr>
                        );
                      })}</tbody>
                    </table>
                  </div>
                </section>

                <section style={{ padding: 16, borderRadius: 14, background: '#fff', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px', color: '#172554' }}>📊 Öğrenci neti ve ortalamalar</h4>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, fontSize: '0.72rem', fontWeight: 800 }}>
                    <span style={{ color: '#dc2626' }}>● Öğrenci</span><span style={{ color: '#0891b2' }}>● Sınıf</span><span style={{ color: '#9333ea' }}>● Kurum</span><span style={{ color: '#2563eb' }}>● Türkiye geneli</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 12, alignItems: 'stretch' }}>
                    {dersKayitlari.map(([ders, sonucDers]) => {
                      const info = sonucDers && typeof sonucDers === 'object' ? sonucDers : { net: sonucDers };
                      const scale = Math.max(1, Number(info.soru) || 0);
                      const measures = [
                        ['Öğrenci', Number(info.net ?? 0), '#dc2626'],
                        ['Sınıf', Number(info.sinif_ortalamasi ?? 0), '#0891b2'],
                        ['Kurum', Number(info.kurum_ortalamasi ?? 0), '#9333ea'],
                        ['Türkiye', Number(info.genel_ortalama ?? 0), '#2563eb'],
                      ];
                      return (
                        <div key={ders} style={{ padding: '12px 9px 9px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <strong style={{ display: 'block', minHeight: 34, color: '#334155', fontSize: '0.76rem', textAlign: 'center' }}>{ders}</strong>
                          <div style={{ height: 132, display: 'flex', justifyContent: 'space-around', alignItems: 'stretch', gap: 3, borderBottom: '1px solid #cbd5e1' }}>
                            {measures.map(([label, value, color]) => (label === 'Öğrenci' || value > 0) && (
                              <div key={label} style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <b style={{ marginBottom: 4, color, fontSize: '0.62rem', whiteSpace: 'nowrap' }}>{netFormatla(value)}</b>
                                <div style={{ width: 'min(26px, 70%)', height: `${Math.max(value > 0 ? 3 : 0, Math.min(100, (value / scale) * 100))}px`, background: color, borderRadius: '5px 5px 0 0', transition: 'height 180ms ease' }} />
                                <span style={{ height: 22, paddingTop: 5, color: '#64748b', fontSize: '0.56rem', textAlign: 'center', whiteSpace: 'nowrap' }}>{label === 'Türkiye' ? 'TR' : label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {ortalamalar.sinif > 0 && (
                    <div style={{ marginTop: 12, padding: 10, borderRadius: 9, background: '#f1f5f9', color: '#334155', fontSize: '0.78rem', fontWeight: 800 }}>
                      Toplam net: <span style={{ color: '#dc2626' }}>{netFormatla(sonuc.toplam_net)}</span> · Sınıf ortalaması: <span style={{ color: '#0891b2' }}>{netFormatla(ortalamalar.sinif)}</span> · Kurum: <span style={{ color: '#9333ea' }}>{netFormatla(ortalamalar.kurum)}</span> · Türkiye geneli: <span style={{ color: '#2563eb' }}>{netFormatla(ortalamalar.genel)}</span>
                    </div>
                  )}
                </section>

                {yanlisKonular.length > 0 && (
                  <section style={{ padding: 16, borderRadius: 14, background: '#fff7f7', border: '1px solid #fecaca' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#991b1b' }}>🎯 Yanlış yapılan konular ve kazanımlar</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 9 }}>
                      {yanlisKonular.map((konu, index) => (
                        <div key={`${konu.ders}-${index}`} style={{ padding: 11, borderRadius: 10, background: '#fff', border: '1px solid #fee2e2' }}>
                          <div style={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 900 }}>{konu.ders} · {konu.dogru || 0}/{konu.soru || 0} doğru</div>
                          <div style={{ marginTop: 4, color: '#7f1d1d', fontSize: '0.76rem', fontWeight: 800, lineHeight: 1.45 }}>{konu.konu}</div>
                          <div style={{ marginTop: 6, color: '#b91c1c', fontSize: '0.7rem', fontWeight: 900 }}>{konu.yanlis} yanlış{Math.max(0, Number(konu.soru || 0) - Number(konu.dogru || 0) - Number(konu.yanlis || 0)) > 0 ? ` · ${Math.max(0, Number(konu.soru || 0) - Number(konu.dogru || 0) - Number(konu.yanlis || 0))} boş` : ''}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {konuKayitlari.length > 0 && (
                  <section style={{ padding: 16, borderRadius: 14, background: '#fff', border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#172554' }}>📖 Konu ve kazanım analizi</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {konuKayitlari.map(([ders, konular]) => (
                        <div key={ders} style={{ overflow: 'hidden', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                          <div style={{ padding: '9px 12px', color: '#1e3a8a', background: '#eff6ff', fontWeight: 900, fontSize: '0.78rem' }}>{ders}</div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', minWidth: 540, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                              <colgroup>
                                <col style={{ width: '55%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '9%' }} />
                              </colgroup>
                              <thead>
                                <tr style={{ color: '#475569', background: '#f8fafc' }}>
                                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Konu / kazanım</th>
                                  <th style={{ padding: '8px 5px', textAlign: 'center' }}>Soru</th>
                                  <th style={{ padding: '8px 5px', textAlign: 'center' }}>Doğru</th>
                                  <th style={{ padding: '8px 5px', textAlign: 'center' }}>Yanlış</th>
                                  <th style={{ padding: '8px 5px', textAlign: 'center' }}>Boş</th>
                                  <th style={{ padding: '8px 5px', textAlign: 'center' }}>Başarı</th>
                                </tr>
                              </thead>
                              <tbody>
                                {konular.map((konu, index) => {
                                  const soru = Number(konu.soru) || 0;
                                  const dogru = Number(konu.dogru) || 0;
                                  const yanlis = Number(konu.yanlis) || 0;
                                  const bos = Math.max(0, soru - dogru - yanlis);
                                  const basari = soru > 0 ? Math.round((dogru / soru) * 100) : 0;
                                  return (
                                    <tr key={`${ders}-${index}`} style={{ background: index % 2 === 0 ? '#fff' : '#f1f5f9' }}>
                                      <td style={{ padding: '8px 10px', color: '#334155', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{konu.konu}</td>
                                      {[soru, dogru, yanlis, bos].map((value, valueIndex) => (
                                        <td key={`${ders}-${index}-${valueIndex}`} style={{ padding: '8px 5px', color: valueIndex === 2 && value > 0 ? '#b91c1c' : '#334155', fontWeight: valueIndex === 2 && value > 0 ? 800 : 600, textAlign: 'center' }}>{value}</td>
                                      ))}
                                      <td style={{ padding: '8px 5px', color: basari >= 70 ? '#047857' : basari >= 40 ? '#b45309' : '#b91c1c', fontWeight: 800, textAlign: 'center' }}>%{basari}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
